import { t } from '@lingui/core/macro';
import { type InspectionSessionRecord } from '@navio/nsdk';
import { NoddiEmptyContent } from 'noddi-ui-web';
import { resolveJobStages, type ResolvedJobStage } from 'noddi-util';
import { InspectionCard, inspectionCardPropsFromResolved } from 'noddi-web';
import { getCommonTranslations, getWheelLabels } from '../../utils/commonTranslations';

interface WheelInspectionProps {
  sessions: InspectionSessionRecord[];
}

const hasVisualInspection = (resolved: ResolvedJobStage<InspectionSessionRecord>) =>
  resolved.specifications.length > 0 || resolved.wheels.some((wheel) => wheel.photo != null);

type StageView =
  | { key: 'preJob' | 'postJob'; kind: 'skip'; reason: string }
  | { key: 'preJob' | 'postJob'; kind: 'card'; resolved: ResolvedJobStage<InspectionSessionRecord> };

export const WheelInspection = ({ sessions }: WheelInspectionProps) => {
  const commonTranslations = getCommonTranslations();
  const wheelLabels = getWheelLabels();

  // Pre job then post job. Tread-only stages stay on the measurement cards.
  const stages = resolveJobStages(sessions).flatMap((resolved): StageView[] => {
    if (resolved.skipReason) {
      return [{ key: resolved.key, kind: 'skip', reason: resolved.skipReason }];
    }
    if (!hasVisualInspection(resolved)) {
      return [];
    }
    return [{ key: resolved.key, kind: 'card', resolved }];
  });

  if (stages.length === 0) {
    return null;
  }

  return (
    <div className='grid grid-cols-1 gap-4 2xl:grid-cols-2'>
      {stages.map((stage) => {
        const when =
          stage.key === 'preJob'
            ? commonTranslations.inspectionCard.beforeService
            : commonTranslations.inspectionCard.afterService;
        if (stage.kind === 'skip') {
          const reason = stage.reason;
          return <NoddiEmptyContent key={stage.key} title={when} message={t`Inspection skipped. ${reason}`} />;
        }
        return (
          <InspectionCard
            key={stage.key}
            {...inspectionCardPropsFromResolved(stage.resolved, wheelLabels)}
            translations={commonTranslations.inspectionCard}
            seasonBadgeTranslations={commonTranslations.seasonBadge}
          />
        );
      })}
    </div>
  );
};
